import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
} from "@mui/material";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { ArrowForward, Close } from "@mui/icons-material";
import { categorie, ticketService, tipo } from "../services/ticketService";
import { showSnackbar } from "../../../../redux/slices/snackbar";
import { useDispatch } from "react-redux";

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing(4, 6)};
`;

const DrawerContent = styled(Box)`
  padding: ${(props) => props.theme.spacing(6)};
  flex: 1;
  overflow-y: auto;
`;

const DrawerFooter = styled(Box)`
  padding: ${(props) => props.theme.spacing(4, 6)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const SupportDrawer = ({ open, setOpen }) => {
  const { options: filters } = useSelector((state) => state.issueFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const name = sessionStorage.getItem("nome");
  const cognome = sessionStorage.getItem("cognome");
  const email = sessionStorage.getItem("email");
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    oggetto: "",
    descrizione: "",
    customerName: `${name} ${cognome}`,
    customerEmail: email,
    categoria: "",
    tipo: "",
  });

  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message: message, severity: severity }));
  };

  // Fallback to static data if global filters are empty or labels don't match
  const normalizedFilters = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return [];
    return filters.map((f) => ({
      ...f,
      dataset:
        f.type === "list" && f.dataset?.dataset ? f.dataset.dataset : f.dataset,
    }));
  }, [filters]);

  const foundCategoria = normalizedFilters.find(
    (f) => f.label?.toLowerCase() === "categoria",
  );

  const categoriaOptions = Array.isArray(foundCategoria?.dataset)
    ? foundCategoria.dataset
    : categorie.map((c) => ({ id: c, label: c, value: c }));

  const foundTipo = normalizedFilters.find(
    (f) => f.label?.toLowerCase() === "tipo",
  );

  const tipoOptions = Array.isArray(foundTipo?.dataset)
    ? foundTipo.dataset
    : tipo.map((t) => ({ id: t, label: t, value: t }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.oggetto || !formData.descrizione) return;

    setLoading(true);
    try {
      const newTicket = {
        oggetto: formData.oggetto,
        descrizione: formData.descrizione,
        tipo: formData.tipo,
        categoria: formData.categoria,
      };

      const responseData = await ticketService.handleAddTicket(newTicket);
      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
      } else {
        handleSnackbar(responseData.message, "error");
      }
      setOpen(false);
      // Reset form
      setFormData({
        ...formData,
        oggetto: "",
        descrizione: "",
        categoria: "",
        tipo: "",
      });
      // Optionally navigate or refresh list
      /* window.location.reload(); */
    } catch (err) {
      console.error("Error creating ticket:", err);
    } finally {
      setLoading(false);
    }
  };

  console.log(formData);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 450 } },
      }}
    >
      <DrawerHeader>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Richiedi supporto
          </Typography>
        </Box>
        <IconButton onClick={() => setOpen(false)} size="small">
          <Close />
        </IconButton>
      </DrawerHeader>

      <Divider />

      <DrawerContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box py={5} textAlign="center">
            <Typography color="error">{error}</Typography>
            <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>
              Riprova
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Grid container direction="row" alignItems="center" mb={2}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "#f1f5f9",
                      color: "#475569",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    {formData.customerName.substring(0, 2).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                      }}
                    >
                      {formData.customerName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: 160,
                      }}
                    >
                      {formData.customerEmail}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      value={formData.categoria}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          categoria: e.target.value,
                        })
                      }
                      label="Categoria"
                      name="categoria"
                    >
                      {categoriaOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={formData.tipo}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tipo: e.target.value,
                        })
                      }
                      label="Tipo"
                      name="tipo"
                    >
                      {tipoOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label="Oggetto del problema"
                name="oggetto"
                required
                placeholder="Esempio: Errore 500 durante il salvataggio..."
                value={formData.oggetto}
                onChange={(e) =>
                  setFormData({ ...formData, oggetto: e.target.value })
                }
                fullWidth
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                required
                multiline
                rows={6}
                placeholder="Cosa è successo? Quali passaggi hai seguito? Ci sono codici di errore?"
                value={formData.descrizione}
                onChange={(e) =>
                  setFormData({ ...formData, descrizione: e.target.value })
                }
                fullWidth
                label="Descrizione dettagliata"
                name="descrizione"
              />
            </Box>
          </Box>
        )}
      </DrawerContent>

      <Divider />

      <DrawerFooter>
        <Button
          fullWidth
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={handleSubmit}
          /* disabled={loading || !data} */
        >
          Invia
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default SupportDrawer;
